import React from 'react';
import { styled } from '@stitches/react';
import AnimatedSection from '../../components/common/AnimatedSection';

// 主标题风格
const MainTitle = styled('h1', {
  fontSize: '2.5rem',
  fontWeight: '700',
  color: '#fff',
  textAlign: 'center',
  margin: '0 0 2rem'
});

// 内容区块样式
const ContentSection = styled('div', {
  backgroundColor: '#232b39',
  borderRadius: '12px',
  padding: '2rem',
  marginBottom: '3rem',
  boxShadow: '0 4px 20px rgba(0, 0, 0, 0.15)',
  transition: 'transform 0.25s cubic-bezier(.4,2,.6,1), box-shadow 0.25s cubic-bezier(.4,2,.6,1);',
  '@media (hover: hover) and (pointer: fine)': {
    '&:hover': {
      transform: 'translateY(-6px) scale(1.03)',
      boxShadow: '0 10px 32px rgba(0,0,0,0.22)',
    }
  },
  '@media (max-width: 768px)': {
    width: '100%',
    padding: '1rem',
    marginLeft: 0,
    marginRight: 0,
  }
});

// 添加两列布局容器
const TwoColumnLayout = styled('div', {
  display: 'grid',
  gridTemplateColumns: '1fr 1fr',
  gap: '2rem',
  alignItems: 'center',
  
  '@media (max-width: 768px)': {
    gridTemplateColumns: '1fr',
    display: 'flex',
    flexDirection: 'column-reverse',
  }
});

// 文字内容列
const TextColumn = styled('div', {
  display: 'flex',
  flexDirection: 'column',
  gap: '1rem',
  
  '@media (max-width: 768px)': {
    order: 2, // 在移动端，文字在底部
  }
});

// 段落标题
const SectionTitle = styled('h2', {
  fontSize: '2.25rem',
  fontWeight: '700',
  color: '#fff',
  marginBottom: '1.5rem',
  
  '@media (max-width: 768px)': {
    order: 0, // 确保在移动端标题在最上方
  }
});

// 段落副标题已删除 - 当前未使用

// 段落文本
const Paragraph = styled('p', {
  fontSize: '1.25rem',
  lineHeight: '1.7',
  color: '#e0e6ed',
  marginBottom: '1.25rem'
});

// 添加图片容器样式
const IllustrationContainer = styled('div', {
  width: '100%',
  display: 'flex',
  justifyContent: 'center',
  overflow: 'hidden',
  borderRadius: '12px',
  
  '@media (max-width: 768px)': {
    order: 1, // 在移动端，图片在中间
    marginBottom: '1.5rem',
  }
});

// 图片样式
const Illustration = styled('img', {
  maxWidth: '100%',
  height: 'auto',
  objectFit: 'cover',
  borderRadius: '12px',
  boxShadow: '0 8px 30px rgba(0, 0, 0, 0.1)',
  transition: 'transform 0.3s ease',
  
  '&:hover': {
    transform: 'scale(1.02)',
  },
  
  '@media (max-width: 768px)': {
    width: '85%', // 移动端设置图片宽度为85%
  }
});

// 横幅区域
const Banner = styled('div', {
  backgroundColor: '#232b39',
  borderRadius: '12px',
  padding: '3rem 2rem',
  marginBottom: '3rem',
  position: 'relative',
  borderTop: '4px solid #6A7EFC',
  boxShadow: '0 4px 20px rgba(0, 0, 0, 0.15)',
  transition: 'transform 0.25s cubic-bezier(.4,2,.6,1), box-shadow 0.25s cubic-bezier(.4,2,.6,1);',
  '@media (hover: hover) and (pointer: fine)': {
    '&:hover': {
      transform: 'translateY(-6px) scale(1.03)',
      boxShadow: '0 10px 32px rgba(0,0,0,0.22)',
    }
  },
  '@media (max-width: 768px)': {
    width: '100%',
    padding: '1.5rem 1rem',
    marginLeft: 0,
    marginRight: 0,
  }
});

// 横幅标题
const BannerTitle = styled('h2', {
  fontSize: '2rem',
  fontWeight: '700',
  color: '#fff',
  marginBottom: '1rem'
});

// 横幅副标题
const BannerSubtitle = styled('p', {
  fontSize: '1.2rem',
  lineHeight: '1.6',
  color: '#e0e6ed',
  maxWidth: '800px'
});

// 基础特性行样式
const FeatureRow = styled('div', {
  backgroundColor: '#232b39',
  borderRadius: '12px',
  padding: '2.5rem',
  marginBottom: '2.5rem',
  boxShadow: '0 8px 25px rgba(0, 0, 0, 0.15)',
  display: 'flex',
  flexDirection: 'row',
  gap: '2.5rem',
  alignItems: 'center',
  transition: 'transform 0.25s cubic-bezier(.4,2,.6,1), box-shadow 0.25s cubic-bezier(.4,2,.6,1);',
  position: 'relative',
  overflow: 'hidden',
  '@media (hover: hover) and (pointer: fine)': {
    '&:hover': {
      transform: 'translateY(-6px) scale(1.03)',
      boxShadow: '0 15px 40px rgba(0,0,0,0.24)',
    }
  },
  '@media (max-width: 768px)': {
    flexDirection: 'column',
    padding: '1rem',
    width: '100%',
    marginLeft: 0,
    marginRight: 0,
    '& > .feature-text > .feature-title': {
      display: 'none',
    },
    '& > .feature-title-mobile': {
      marginBottom: '1.5rem',
      paddingBottom: '0.8rem',
      width: '100%',
      display: 'block',
    }
  },
});

// 特性行（反向）
const FeatureRowReverse = styled('div', {
  backgroundColor: '#232b39',
  borderRadius: '12px',
  padding: '2.5rem',
  marginBottom: '2.5rem',
  boxShadow: '0 8px 25px rgba(0, 0, 0, 0.15)',
  display: 'flex',
  flexDirection: 'row-reverse',
  gap: '2.5rem',
  alignItems: 'center',
  transition: 'transform 0.25s cubic-bezier(.4,2,.6,1), box-shadow 0.25s cubic-bezier(.4,2,.6,1);',
  position: 'relative',
  overflow: 'hidden',
  '@media (hover: hover) and (pointer: fine)': {
    '&:hover': {
      transform: 'translateY(-6px) scale(1.03)',
      boxShadow: '0 15px 40px rgba(0,0,0,0.24)',
    }
  },
  '@media (max-width: 768px)': {
    flexDirection: 'column',
    padding: '1rem',
    width: '100%',
    marginLeft: 0,
    marginRight: 0,
    '& > .feature-text > .feature-title': {
      display: 'none',
    },
    '& > .feature-title-mobile': {
      marginBottom: '1.5rem',
      paddingBottom: '0.8rem',
      width: '100%',
      display: 'block',
    }
  },
});

// 移动端标题样式已删除 - 当前未使用

// 特性文本内容容器 - 移回标题和内容组合
const FeatureTextContent = styled('div', {
  flex: '1',
  display: 'flex',
  flexDirection: 'column',
  gap: '1.2rem',
  zIndex: 1,
  
  '@media (max-width: 768px)': {
    width: '100%',
    order: 2, // 确保在移动端文字内容在最下方
  }
});

// 特性图标/图片容器
const FeatureImageContainer = styled('div', {
  flex: '1',
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  overflow: 'hidden',
  borderRadius: '12px',
  maxWidth: '400px',
  zIndex: 1,
  
  '@media (max-width: 768px)': {
    width: '100%',
    maxWidth: '100%',
    marginTop: '1rem',
    marginBottom: '1rem',
    order: 1, // 确保在移动端图片在中间
  }
});

// 特性图片
const FeatureImage = styled('img', {
  width: '60%',
  height: 'auto',
  objectFit: 'cover',
  borderRadius: '12px',
  boxShadow: '0 10px 30px rgba(0, 0, 0, 0.15)',
  transition: 'transform 0.3s ease',
  
  '&:hover': {
    transform: 'scale(1.05)',
  },
  
  '@media (max-width: 768px)': {
    width: '95%',
  }
});

// 特性卡片标题 - 移动端时将单独放置
const FeatureTitle = styled('h3', {
  fontSize: '1.75rem',
  fontWeight: '700',
  color: '#fff',
  position: 'relative',
  paddingBottom: '0.8rem',
  marginBottom: '0.5rem',
  
  '@media (max-width: 768px)': {
    order: 0, // 确保在移动端标题在最上方
    width: '100%',
  }
});

// 特性卡片内容
const FeatureContent = styled('p', {
  fontSize: '1.1rem',
  lineHeight: '1.7',
  color: '#e0e6ed',
  
  '@media (max-width: 768px)': {
    order: 1, // 确保在移动端文字内容在最下方
  }
});

// 自定义样式类名
const featureBlue = {
  row: {
    backgroundColor: 'rgba(106, 126, 252, 0.03)',
    borderLeft: '6px solid #6A7EFC',
  },
  rowReverse: {
    backgroundColor: 'rgba(106, 126, 252, 0.03)',
    borderRight: '6px solid #6A7EFC',
  },
  title: {
    borderBottom: '3px solid #6A7EFC',
  }
};

const featureGreen = {
  row: {
    backgroundColor: 'rgba(76, 175, 80, 0.03)',
    borderLeft: '6px solid #4CAF50',
  },
  rowReverse: {
    backgroundColor: 'rgba(76, 175, 80, 0.03)',
    borderRight: '6px solid #4CAF50',
  },
  title: {
    borderBottom: '3px solid #4CAF50',
  }
};

const featureOrange = {
  row: {
    backgroundColor: 'rgba(255, 152, 0, 0.03)',
    borderLeft: '6px solid #FF9800',
  },
  rowReverse: {
    backgroundColor: 'rgba(255, 152, 0, 0.03)',
    borderRight: '6px solid #FF9800',
  },
  title: {
    borderBottom: '3px solid #FF9800',
  }
};

const featurePink = {
  row: {
    backgroundColor: 'rgba(233, 30, 99, 0.03)',
    borderLeft: '6px solid #E91E63',
  },
  rowReverse: {
    backgroundColor: 'rgba(233, 30, 99, 0.03)',
    borderRight: '6px solid #E91E63',
  },
  title: {
    borderBottom: '3px solid #E91E63',
  }
};

const featurePurple = {
  row: {
    backgroundColor: 'rgba(156, 39, 176, 0.03)',
    borderLeft: '6px solid #9C27B0',
  },
  rowReverse: {
    backgroundColor: 'rgba(156, 39, 176, 0.03)',
    borderRight: '6px solid #9C27B0',
  },
  title: {
    borderBottom: '3px solid #9C27B0',
  }
};

const featureCyan = {
  row: {
    backgroundColor: 'rgba(0, 188, 212, 0.03)',
    borderLeft: '6px solid #00BCD4',
  },
  rowReverse: {
    backgroundColor: 'rgba(0, 188, 212, 0.03)',
    borderRight: '6px solid #00BCD4',
  },
  title: {
    borderBottom: '3px solid #00BCD4',
  }
};

// 添加移动端修饰类
const MobileFeatureStyles = styled('div', {
  '@media (max-width: 768px)': {
    '& > div.feature-row': {
      '& > h3.feature-title': {
        order: 0, // 标题在上
      },
      '& > div.feature-image': {
        order: 1, // 图片在中
      },
      '& > div.feature-text': {
        order: 2, // 文字在下
      }
    }
  }
});

// 合作伙伴区域
const PartnersSection = styled('div', {
  marginTop: '3rem',
  display: 'grid',
  gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))',
  gap: '2rem',
  
  '@media (max-width: 768px)': {
    gridTemplateColumns: '1fr',
  }
});

// 合作伙伴卡片
const PartnerCard = styled('div', {
  backgroundColor: '#232b39',
  borderRadius: '12px',
  padding: '2rem',
  boxShadow: '0 8px 30px rgba(0, 0, 0, 0.15)',
  transition: 'all 0.3s ease, transform 0.25s cubic-bezier(.4,2,.6,1), box-shadow 0.25s cubic-bezier(.4,2,.6,1);',
  border: '1px solid #232b39',
  height: '100%',
  display: 'flex',
  flexDirection: 'column',
  position: 'relative',
  overflow: 'hidden',
  '@media (hover: hover) and (pointer: fine)': {
    '&:hover': {
      transform: 'translateY(-6px) scale(1.03)',
      boxShadow: '0 15px 48px rgba(0,0,0,0.26)',
      borderColor: '#2d3750',
    }
  },
  '@media (max-width: 768px)': {
    padding: '1rem',
    width: '100%',
    marginLeft: 0,
    marginRight: 0,
  },
  '&::before': {
    content: "''",
    position: 'absolute',
    top: '0',
    left: '0',
    width: '100%',
    height: '5px',
    background: 'linear-gradient(90deg, transparent, rgba(106, 126, 252, 0.5), transparent)',
    opacity: '0',
    transition: 'opacity 0.3s ease',
  },
  '&:hover': {
    '&::before': {
      opacity: '1',
    }
  }
});

// 合作伙伴标题
const PartnerName = styled('h3', {
  fontSize: '1.5rem',
  fontWeight: '700',
  color: '#fff',
  marginBottom: '1.25rem',
  position: 'relative',
  paddingBottom: '1rem',
  display: 'flex',
  alignItems: 'center',
  
  '&::after': {
    content: "''",
    position: 'absolute',
    bottom: '0',
    left: '0',
    width: '60px',
    height: '3px',
    background: 'linear-gradient(90deg, #6A7EFC, #9C27B0)',
    borderRadius: '3px',
    transition: 'width 0.3s ease',
  },
  
  '&:hover::after': {
    width: '100px',
  }
});

// 合作伙伴描述
const PartnerDescription = styled('p', {
  fontSize: '1rem',
  lineHeight: '1.7',
  color: '#e0e6ed',
  flex: '1',
});

// 合作伙伴图标
const PartnerIcon = styled('div', {
  width: '64px',
  height: '64px',
  backgroundColor: '#f8f9fc',
  borderRadius: '50%',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  marginBottom: '1.25rem',
  boxShadow: '0 5px 15px rgba(0, 0, 0, 0.08)',
  fontSize: '1.75rem',
  color: '#6A7EFC',
  border: '2px solid #EEF1FF',
  transition: 'all 0.3s ease',
  position: 'relative',
  zIndex: '1',
  
  '&::before': {
    content: "''",
    position: 'absolute',
    inset: '-4px',
    borderRadius: '50%',
    background: 'linear-gradient(135deg, rgba(106, 126, 252, 0.5), rgba(156, 39, 176, 0.5))',
    opacity: '0',
    transition: 'opacity 0.3s ease',
    zIndex: '-1',
  },
  
  '&:hover': {
    transform: 'scale(1.05)',
    
    '&::before': {
      opacity: '0.3',
    }
  }
});

// 证书部分已删除 - 当前未使用

// 证书标题
const CertificateTitle = styled('h2', {
  fontSize: '2.25rem',
  fontWeight: '700',
  color: '#fff',
  textAlign: 'center',
  marginBottom: '2rem'
});

// 证书轮播容器
const CertificateCarousel = styled('div', {
  display: 'grid',
  gridTemplateColumns: 'repeat(3, 1fr)',
  gap: '2rem',
  
  '@media (max-width: 1024px)': {
    gridTemplateColumns: 'repeat(2, 1fr)',
  },
  
  '@media (max-width: 768px)': {
    gridTemplateColumns: '1fr',
  }
});

// 证书卡片
const CertificateCard = styled('div', {
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  textAlign: 'center',
  padding: '1rem',
  borderRadius: '12px',
  backgroundColor: '#232b39',
  boxShadow: '0 8px 30px rgba(0, 0, 0, 0.15)',
  transition: 'transform 0.25s cubic-bezier(.4,2,.6,1), box-shadow 0.25s cubic-bezier(.4,2,.6,1);',
  '@media (hover: hover) and (pointer: fine)': {
    '&:hover': {
      transform: 'translateY(-6px) scale(1.03)',
      boxShadow: '0 15px 48px rgba(0,0,0,0.26)',
    }
  },
  '@media (max-width: 768px)': {
    width: '100%',
    padding: '1rem',
    marginLeft: 0,
    marginRight: 0,
  },
});

// 证书图片容器
const CertificateImageContainer = styled('div', {
  width: '100%',
  marginBottom: '1rem',
  position: 'relative',
  padding: '20px',
  
  '&::before': {
    content: "''",
    position: 'absolute',
    top: '0',
    left: '0',
    right: '0',
    bottom: '0',
    background: 'linear-gradient(145deg, #f8d568, #d4af37)',
    borderRadius: '12px',
    zIndex: 0
  }
});

// 证书内容(蓝色背景部分)
const CertificateContent = styled('div', {
  backgroundColor: '#0a3977',
  color: '#fff',
  padding: '30px 15px',
  borderRadius: '8px',
  position: 'relative',
  zIndex: 1,
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  border: '1px solid #0a3977',
  boxShadow: 'inset 0 0 15px rgba(0, 0, 0, 0.3)',
  overflow: 'hidden',
  
  '&::before': {
    content: "''",
    position: 'absolute',
    top: '10px',
    left: '10px',
    right: '10px',
    bottom: '10px',
    border: '1px solid rgba(212, 175, 55, 0.5)',
    borderRadius: '4px',
    pointerEvents: 'none'
  },
  
  '&::after': {
    content: "''",
    position: 'absolute',
    top: '0',
    left: '0',
    right: '0',
    bottom: '0',
    backgroundImage: 'linear-gradient(0deg, rgba(212, 175, 55, 0.05) 1px, transparent 1px), linear-gradient(90deg, rgba(212, 175, 55, 0.05) 1px, transparent 1px)',
    backgroundSize: '20px 20px',
    pointerEvents: 'none',
    opacity: 0.5
  }
});

// 装饰边框
const DecorativeBorder = styled('div', {
  position: 'absolute',
  top: '0',
  left: '0',
  right: '0',
  bottom: '0',
  pointerEvents: 'none',
  zIndex: 2,
  
  '&::before, &::after, & > *::before, & > *::after': {
    content: "''",
    position: 'absolute',
    width: '20px',
    height: '20px',
    border: '2px solid #d4af37',
    pointerEvents: 'none'
  },
  
  // 左上角装饰
  '&::before': {
    top: '5px',
    left: '5px',
    borderBottom: 'none',
    borderRight: 'none'
  },
  
  // 右上角装饰
  '&::after': {
    top: '5px',
    right: '5px',
    borderBottom: 'none',
    borderLeft: 'none'
  },
  
  // 左下角装饰
  '& > .bottom-left::before': {
    bottom: '5px',
    left: '5px',
    borderTop: 'none',
    borderRight: 'none'
  },
  
  // 右下角装饰
  '& > .bottom-right::before': {
    bottom: '5px',
    right: '5px',
    borderTop: 'none',
    borderLeft: 'none'
  }
});

// 装饰分隔线
const DecorativeDivider = styled('div', {
  width: '80%',
  height: '1px',
  background: 'linear-gradient(90deg, transparent, #d4af37, transparent)',
  margin: '10px 0',
  position: 'relative',
  
  '&::before, &::after': {
    content: "''",
    position: 'absolute',
    width: '5px',
    height: '5px',
    backgroundColor: '#d4af37',
    borderRadius: '50%',
    top: '-2px'
  },
  
  '&::before': {
    left: '30%'
  },
  
  '&::after': {
    right: '30%'
  }
});

// 证书标志
const CertificateBadge = styled('div', {
  width: '50px',
  height: '50px',
  backgroundColor: '#d4af37',
  borderRadius: '50%',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  marginBottom: '10px',
  border: '2px solid #fff',
  boxShadow: '0 2px 8px rgba(0, 0, 0, 0.2)',
  fontSize: '24px',
  fontWeight: 'bold',
  color: '#0a3977'
});

// 证书标题(证书内部)
const CertificateHeading = styled('div', {
  fontSize: '24px',
  fontWeight: 'bold',
  marginBottom: '15px',
  letterSpacing: '1px',
  textTransform: 'uppercase',
  color: '#d4af37'
});

// 证书姓名
const CertificatePerson = styled('div', {
  fontSize: '18px',
  marginBottom: '20px',
  fontStyle: 'italic',
  color: '#fff'
});

// 证书签名行
const CertificateSignature = styled('div', {
  fontSize: '14px',
  marginTop: '20px',
  padding: '10px 0',
  borderTop: '1px solid rgba(255, 255, 255, 0.2)',
  color: '#d4af37'
});

// 证书图片
const CertificateImage = styled('img', {
  display: 'none' // 隐藏原始图片，使用自定义设计
});

// 证书名称
const CertificateName = styled('h3', {
  fontSize: '1.25rem',
  fontWeight: '600',
  color: '#fff',
  marginBottom: '0.5rem'
});

// 证书描述
const CertificateDescription = styled('p', {
  fontSize: '0.9rem',
  lineHeight: '1.6',
  color: '#e0e6ed',
});

// 证书文本已删除 - 当前未使用

// 证书页码指示器
const CertificatePagination = styled('div', {
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  gap: '0.5rem',
  marginTop: '2rem',
});

// 证书页码点
const CertificatePaginationDot = styled('span', {
  width: '10px',
  height: '10px',
  borderRadius: '50%',
  backgroundColor: '#ddd',
  
  '&.active': {
    backgroundColor: '#6A7EFC',
  }
});

// 为移动端标题添加PC端隐藏样式
const MobileFeatureTitle = styled(FeatureTitle, {
  display: 'none',
  
  '@media (max-width: 768px)': {
    display: 'block',
    marginBottom: '1.5rem',
    paddingBottom: '0.8rem',
    width: '100%',
  }
});

/**
 * 关于公司页面组件
 * 展示公司简介、愿景使命、企业文化和发展历程等信息
 */
const AboutCompany: React.FC = () => {
  // const { t } = useTranslation(); // 暂时未使用
  
  return (
    <>
      <MainTitle>About BITENGINE</MainTitle>
      
      <AnimatedSection direction="up">
        <ContentSection>
          <TwoColumnLayout>
            <TextColumn>
              <SectionTitle>引领数字挖矿新时代</SectionTitle>
              <Paragraph>
                BITENGINE 成立于 2018 年，是全球领先的数字货币挖矿服务提供商。我们致力于为用户提供安全、便捷、高效的挖矿解决方案，让每个人都能轻松参与数字经济的未来。
              </Paragraph>
            </TextColumn>
            <IllustrationContainer>
              <Illustration src="/images/about1.jpeg" alt="BITENGINE Mining Facility" />
            </IllustrationContainer>
          </TwoColumnLayout>
        </ContentSection>
      </AnimatedSection>
      
      <AnimatedSection direction="up">
        <Banner>
          <BannerTitle>Mining at your fingertips with BeMine</BannerTitle>
          <BannerSubtitle>
            Unleash the power of crypto mining without the hassle. Forget the complexities of assembling your mining rig. Now you can dive into the world of mining without the need to gather hardware.
          </BannerSubtitle>
        </Banner>
      </AnimatedSection>
      
      <AnimatedSection direction="up">
        <ContentSection>
          <SectionTitle>Our Features</SectionTitle>
          
          <MobileFeatureStyles>
            {/* 修复重复标题问题 */}
            <AnimatedSection direction="up">
              <FeatureRow className="feature-row" style={featureBlue.row}>
                <MobileFeatureTitle className="feature-title-mobile" style={featureBlue.title}>No equipment, no worries</MobileFeatureTitle>
                <FeatureTextContent className="feature-text">
                  <FeatureTitle className="feature-title" style={featureBlue.title}>No equipment, no worries</FeatureTitle>
                  <FeatureContent>
                    We try to redefine the mining experience, putting the power of crypto mining directly at your fingertips. Our commitment to simplicity and convenience makes us the go-to choice for both beginners and seasoned miners alike.
                  </FeatureContent>
                </FeatureTextContent>
                <FeatureImageContainer className="feature-image">
                  <FeatureImage src="/images/about2.jpeg" alt="No equipment, no worries" />
                </FeatureImageContainer>
              </FeatureRow>
            </AnimatedSection>
            
            <AnimatedSection direction="up">
              <FeatureRowReverse className="feature-row" style={featureGreen.rowReverse}>
                <MobileFeatureTitle className="feature-title-mobile" style={featureGreen.title}>Seamless user experience</MobileFeatureTitle>
                <FeatureTextContent className="feature-text">
                  <FeatureTitle className="feature-title" style={featureGreen.title}>Seamless user experience</FeatureTitle>
                  <FeatureContent>
                    User-friendly platform ensures that mining is a straightforward and enjoyable experience and makes managing your mining activities simple.
                  </FeatureContent>
                </FeatureTextContent>
                <FeatureImageContainer className="feature-image">
                  <FeatureImage src="/images/about3.jpeg" alt="Seamless user experience" />
                </FeatureImageContainer>
              </FeatureRowReverse>
            </AnimatedSection>
            
            <AnimatedSection direction="up">
              <FeatureRow className="feature-row" style={featureOrange.row}>
                <MobileFeatureTitle className="feature-title-mobile" style={featureOrange.title}>Start small with 1/100 ASICs</MobileFeatureTitle>
                <FeatureTextContent className="feature-text">
                  <FeatureTitle className="feature-title" style={featureOrange.title}>Start small with 1/100 ASICs</FeatureTitle>
                  <FeatureContent>
                    Begin your mining adventure by purchasing a fraction, as little as 1/100, of our efficient ASICs. Start small, grow big – the choice is yours.
                  </FeatureContent>
                </FeatureTextContent>
                <FeatureImageContainer className="feature-image">
                  <FeatureImage src="/images/about4.jpeg" alt="Start small with 1/100 ASICs" />
                </FeatureImageContainer>
              </FeatureRow>
            </AnimatedSection>
            
            <AnimatedSection direction="up">
              <FeatureRowReverse className="feature-row" style={featurePink.rowReverse}>
                <MobileFeatureTitle className="feature-title-mobile" style={featurePink.title}>Free trial for a taste</MobileFeatureTitle>
                <FeatureTextContent className="feature-text">
                  <FeatureTitle className="feature-title" style={featurePink.title}>Free trial for a taste</FeatureTitle>
                  <FeatureContent>
                    Curious about experience? Take advantage of free trial and explore all the conveniences our platform offers. Test the waters before diving into the full mining experience.
                  </FeatureContent>
                </FeatureTextContent>
                <FeatureImageContainer className="feature-image">
                  <FeatureImage src="/images/about5.jpeg" alt="Free trial for a taste" />
                </FeatureImageContainer>
              </FeatureRowReverse>
            </AnimatedSection>
            
            <AnimatedSection direction="up">
              <FeatureRow className="feature-row" style={featurePurple.row}>
                <MobileFeatureTitle className="feature-title-mobile" style={featurePurple.title}>Zero withdrawal fees</MobileFeatureTitle>
                <FeatureTextContent className="feature-text">
                  <FeatureTitle className="feature-title" style={featurePurple.title}>Zero withdrawal fees</FeatureTitle>
                  <FeatureContent>
                    Enjoy the full benefits of your mining rewards with BeMine. We charge 0% for withdrawal commissions, allowing you to transfer your earnings to any wallet of your choice.
                  </FeatureContent>
                </FeatureTextContent>
                <FeatureImageContainer className="feature-image">
                  <FeatureImage src="/images/about6.jpeg" alt="Zero withdrawal fees" />
                </FeatureImageContainer>
              </FeatureRow>
            </AnimatedSection>
            
            <AnimatedSection direction="up">
              <FeatureRowReverse className="feature-row" style={featureCyan.rowReverse}>
                <MobileFeatureTitle className="feature-title-mobile" style={featureCyan.title}>Transparent maintenance costs</MobileFeatureTitle>
                <FeatureTextContent className="feature-text">
                  <FeatureTitle className="feature-title" style={featureCyan.title}>Transparent maintenance costs</FeatureTitle>
                  <FeatureContent>
                    Our service fee is all-inclusive and clearly displayed for your convenience. See the costs associated with electricity, regular maintenance, and equipment security separately, providing full clarity.
                  </FeatureContent>
                </FeatureTextContent>
                <FeatureImageContainer className="feature-image">
                  <FeatureImage src="/images/about7.jpeg" alt="Transparent maintenance costs" />
                </FeatureImageContainer>
              </FeatureRowReverse>
            </AnimatedSection>
          </MobileFeatureStyles>
        </ContentSection>
      </AnimatedSection>
      
      <AnimatedSection direction="up">
        <ContentSection>
          <SectionTitle style={{ textAlign: 'center', marginBottom: '2.5rem' }}>Trusted partners for ultimate experience delivery</SectionTitle>
          
          <PartnersSection>
            <PartnerCard style={{ borderLeft: '3px solid #6A7EFC' }}>
              <PartnerIcon style={{ backgroundColor: 'rgba(106, 126, 252, 0.08)', borderColor: 'rgba(106, 126, 252, 0.2)', color: '#6A7EFC' }}>
                <svg width="32" height="32" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M5 5V19H19V5H5ZM4 3H20C20.5523 3 21 3.44772 21 4V20C21 20.5523 20.5523 21 20 21H4C3.44772 21 3 20.5523 3 20V4C3 3.44772 3.44772 3 4 3ZM11 11V17H7V11H11ZM11 7V9H7V7H11ZM17 11V17H13V11H17ZM17 7V9H13V7H17Z" fill="currentColor"/>
                </svg>
              </PartnerIcon>
              <PartnerName>
                <span style={{ color: '#6A7EFC', marginRight: '8px' }}>#</span>
                KobakLab
              </PartnerName>
              <PartnerDescription>
                KobakLab is a development studio creating innovative products in the field of IT and robotics since 2014. The company is making software development, hardware and software systems, as well as integrated project management. The portfolio includes more than 120 projects in total.
              </PartnerDescription>
            </PartnerCard>
            
            <PartnerCard style={{ borderLeft: '3px solid #4CAF50' }}>
              <PartnerIcon style={{ backgroundColor: 'rgba(76, 175, 80, 0.08)', borderColor: 'rgba(76, 175, 80, 0.2)', color: '#4CAF50' }}>
                <svg width="32" height="32" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M12 2C17.5228 2 22 6.47715 22 12C22 17.5228 17.5228 22 12 22C6.47715 22 2 17.5228 2 12C2 6.47715 6.47715 2 12 2ZM12 4C7.58172 4 4 7.58172 4 12C4.00001 16.4183 7.58173 20 12 20C16.4183 20 20 16.4183 20 12C20 7.58172 16.4183 4 12 4ZM12 6C13.1046 6 14 6.89543 14 8L10 8C10 6.89543 10.8954 6 12 6ZM14 14C14 14.5523 13.5523 15 13 15C12.4477 15 12 14.5523 12 14H8C8 16.7614 10.2386 19 13 19C15.7614 19 18 16.7614 18 14H14Z" fill="currentColor"/>
                </svg>
              </PartnerIcon>
              <PartnerName>
                <span style={{ color: '#4CAF50', marginRight: '8px' }}>#</span>
                GMT Legal
              </PartnerName>
              <PartnerDescription>
                GMT Legal is a leading law firm in Russia and the CIS in the field of IT, FinTech, Gamedev. Since 2017, we have provided legal support for more than 300 blockchain projects on corporate structure, tax optimization and licensing in the USA, EU countries and Asia. GMT Legal is included in the Russian and international rankings of Chambers and Partners (FinTech Legal), Pravo-300 (Digital Economy), Best Lawyers.
              </PartnerDescription>
            </PartnerCard>
            
            <PartnerCard style={{ borderLeft: '3px solid #FF9800' }}>
              <PartnerIcon style={{ backgroundColor: 'rgba(255, 152, 0, 0.08)', borderColor: 'rgba(255, 152, 0, 0.2)', color: '#FF9800' }}>
                <svg width="32" height="32" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M2 9.5V4C2 3.44772 2.44772 3 3 3H21C21.5523 3 22 3.44772 22 4V9.5C22 10.0523 21.5523 10.5 21 10.5H3C2.44772 10.5 2 10.0523 2 9.5ZM9 7C9 7.55228 9.44771 8 10 8C10.5523 8 11 7.55228 11 7C11 6.44772 10.5523 6 10 6C9.44771 6 9 6.44772 9 7ZM13 7C13 7.55228 13.4477 8 14 8C14.5523 8 15 7.55228 15 7C15 6.44772 14.5523 6 14 6C13.4477 6 13 6.44772 13 7ZM17 7C17 7.55228 17.4477 8 18 8C18.5523 8 19 7.55228 19 7C19 6.44772 18.5523 6 18 6C17.4477 6 17 6.44772 17 7ZM5 7C5 7.55228 5.44772 8 6 8C6.55228 8 7 7.55228 7 7C7 6.44772 6.55228 6 6 6C5.44772 6 5 6.44772 5 7ZM2 16V12C2 11.4477 2.44772 11 3 11H21C21.5523 11 22 11.4477 22 12V16C22 16.5523 21.5523 17 21 17H3C2.44772 17 2 16.5523 2 16ZM2 20V18.5C2 17.9477 2.44772 17.5 3 17.5H21C21.5523 17.5 22 17.9477 22 18.5V20C22 20.5523 21.5523 21 21 21H3C2.44772 21 2 20.5523 2 20Z" fill="currentColor"/>
                </svg>
              </PartnerIcon>
              <PartnerName>
                <span style={{ color: '#FF9800', marginRight: '8px' }}>#</span>
                Unistar mining
              </PartnerName>
              <PartnerDescription>
                Unistar, founded in Shenzhen, China, is one of the first institutions in China to engage in bitcoin-related investment activities. In 2015, we built our first mining farm in Sichuan Province, China. With the development of this business, we have established close partnerships with more than 500 mining farm owners around the world, and successively built 6 new mining farms with our partners in the United States, Russia, Thailand and Indonesia. As of June 2022, we have sold more than 420,000 used miners and more than 160,000 brand new miners to the world. With the rapid growth of our business, Unistar has developed into an important partner of Bitmain (Antminer), MicroBT(Whatsminer), Canaan (Avalon).
              </PartnerDescription>
            </PartnerCard>
          </PartnersSection>
        </ContentSection>
      </AnimatedSection>
      
      <AnimatedSection direction="up">
        <ContentSection>
          <CertificateTitle>资质证书</CertificateTitle>
          
          <CertificateCarousel>
            <CertificateCard>
              <CertificateImageContainer>
                <CertificateContent>
                  <CertificateBadge>2</CertificateBadge>
                  <div style={{ fontSize: '12px', marginBottom: '5px', color: '#d4af37' }}>PRUSESTENTE</div>
                  <CertificateHeading>CERTIFICATE</CertificateHeading>
                  <DecorativeDivider />
                  <CertificatePerson>Harry Jochen</CertificatePerson>
                  <div style={{ fontSize: '10px', color: '#eee', textAlign: 'center', marginBottom: '15px' }}>
                    The veloci fi-or vealienity thggreates.
                  </div>
                  <DecorativeDivider />
                  <CertificateSignature>Most N'Keal Castle</CertificateSignature>
                  <div style={{ fontSize: '10px', color: '#eee', textAlign: 'center' }}>
                    The sears neiicates a neitse of ensgysneice.
                  </div>
                  <DecorativeBorder>
                    <div className="bottom-left"></div>
                    <div className="bottom-right"></div>
                  </DecorativeBorder>
                </CertificateContent>
                <CertificateImage src="/images/certificate1.jpg" alt="专业认证证书" />
              </CertificateImageContainer>
              <CertificateName>专业认证证书</CertificateName>
              <CertificateDescription>国际区块链协会认证</CertificateDescription>
            </CertificateCard>
            
            <CertificateCard>
              <CertificateImageContainer>
                <CertificateContent>
                  <CertificateBadge>2</CertificateBadge>
                  <div style={{ fontSize: '12px', marginBottom: '5px', color: '#d4af37' }}>PRUSESTENTE</div>
                  <CertificateHeading>CERTIFICATE</CertificateHeading>
                  <DecorativeDivider />
                  <CertificatePerson>Harry Jochen</CertificatePerson>
                  <div style={{ fontSize: '10px', color: '#eee', textAlign: 'center', marginBottom: '15px' }}>
                    The veloci fi-or vealienity thggreates.
                  </div>
                  <DecorativeDivider />
                  <CertificateSignature>Most N'Keal Castle</CertificateSignature>
                  <div style={{ fontSize: '10px', color: '#eee', textAlign: 'center' }}>
                    The sears neiicates a neitse of ensgysneice.
                  </div>
                  <DecorativeBorder>
                    <div className="bottom-left"></div>
                    <div className="bottom-right"></div>
                  </DecorativeBorder>
                </CertificateContent>
                <CertificateImage src="/images/certificate2.jpg" alt="专业认证证书" />
              </CertificateImageContainer>
              <CertificateName>专业认证证书</CertificateName>
              <CertificateDescription>国际区块链协会认证</CertificateDescription>
            </CertificateCard>
            
            <CertificateCard>
              <CertificateImageContainer>
                <CertificateContent>
                  <CertificateBadge>2</CertificateBadge>
                  <div style={{ fontSize: '12px', marginBottom: '5px', color: '#d4af37' }}>PRUSESTENTE</div>
                  <CertificateHeading>CERTIFICATE</CertificateHeading>
                  <DecorativeDivider />
                  <CertificatePerson>Harry Jochen</CertificatePerson>
                  <div style={{ fontSize: '10px', color: '#eee', textAlign: 'center', marginBottom: '15px' }}>
                    The veloci fi-or vealienity thggreates.
                  </div>
                  <DecorativeDivider />
                  <CertificateSignature>Most N'Keal Castle</CertificateSignature>
                  <div style={{ fontSize: '10px', color: '#eee', textAlign: 'center' }}>
                    The sears neiicates a neitse of ensgysneice.
                  </div>
                  <DecorativeBorder>
                    <div className="bottom-left"></div>
                    <div className="bottom-right"></div>
                  </DecorativeBorder>
                </CertificateContent>
                <CertificateImage src="/images/certificate3.jpg" alt="专业认证证书" />
              </CertificateImageContainer>
              <CertificateName>专业认证证书</CertificateName>
              <CertificateDescription>国际区块链协会认证</CertificateDescription>
            </CertificateCard>
          </CertificateCarousel>
          
          <CertificatePagination>
            <CertificatePaginationDot className="active" />
            <CertificatePaginationDot />
            <CertificatePaginationDot />
          </CertificatePagination>
        </ContentSection>
      </AnimatedSection>
    </>
  );
};

export default AboutCompany; 